import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import produce from "immer";
import { recordActionAsEnum, recordActionFromEnum } from "psims/models/api/data-submission-record-action";
import { cleanSubmissionEntityList } from "../../../shared/update-data-utils";
import { isEmpty } from "psims/lib/empty";
import { UseFocusedField } from "psims/react/util/use-focused-field";
import { FocusField } from "../types";
import { validateStorage, StorageValidationResult, StorageValidationField } from "./enforceable-arrangements-validation";
import { getStorageInlineMessage } from "./enforceable-arrangements-inline-messages";
import { getStorageNotificationMessage } from "./enforceable-arrangments-notfications";
import { FieldValidation } from "../types";

export interface EnforceableArrangement {
  id?: number;
  _id: symbol;
  _key: string;
  rowFocusField?: any;
  dataSubmissionId: number;
  msoProductId?: number;
  ownerName?: string;
  beneficiary?: string;
  stockOwnershipAgreementType?: number;
  volume?: number;
  obligationDate?: string | null;
  startDateOfThisArrangement?: string | null;
  endDateOfThisArrangement?: string | null;
  recordAction?: number;
}

let _keyCounter = 0;
const newKey = () => `ea_${++_keyCounter}`;

function stampRecords(
  raw: EnforceableArrangement[],
  keyMap: Map<number, string>
): EnforceableArrangement[] {
  return (raw || []).map(x => {
    if (x.id != null && !keyMap.has(x.id)) {
      keyMap.set(x.id, newKey());
    }
    const stableKey = x.id != null ? keyMap.get(x.id)! : newKey();
    return {
      ...x,
      _id: Symbol(),
      _key: stableKey,
      recordAction: x.recordAction ?? undefined,
    };
  });
}

interface UseEnforceableArrangementsProps {
  initial: EnforceableArrangement[];
  dataSubmissionId: number;
  focusFieldCtrl: UseFocusedField<FocusField>;
  products: Array<{ id: number; name: string; productStatus?: string }>;
}

function useEnforceableArrangements({
  initial,
  dataSubmissionId,
  focusFieldCtrl,
  products,
}: UseEnforceableArrangementsProps) {
  const keyMapRef = useRef<Map<number, string>>(new Map());

  const [data, setData] = useState<EnforceableArrangement[]>(() =>
    stampRecords(initial, keyMapRef.current)
  );

  const serverIdSignature = useMemo(
    () => (initial || []).map(x => x.id ?? 'new').join(','),
    [initial]
  );
  const prevSignatureRef = useRef(serverIdSignature);

  useEffect(() => {
    if (serverIdSignature !== prevSignatureRef.current) {
      prevSignatureRef.current = serverIdSignature;
      setData(stampRecords(initial, keyMapRef.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverIdSignature]);

  const add = useCallback((msoProductId?: number) => {
    setData(prev => produce(prev, draft => {
      draft.push({
        _id: Symbol(),
        _key: newKey(),
        dataSubmissionId,
        msoProductId: msoProductId ?? undefined,
        ownerName: undefined,
        beneficiary: undefined,
        stockOwnershipAgreementType: undefined,
        volume: undefined,
        obligationDate: null,
        startDateOfThisArrangement: null,
        endDateOfThisArrangement: null,
        recordAction: recordActionAsEnum('Create'),
      });
    }));
  }, [dataSubmissionId]);

  const update = useCallback((row: EnforceableArrangement) => {
    setData(prev => produce(prev, draft => {
      const i = draft.findIndex(d => d._id === row._id);
      if (i === -1) return;
      draft[i] = {
        ...row,
        recordAction: recordActionAsEnum(row.id ? 'Update' : 'Create'),
      };
    }));
  }, []);

  const remove = useCallback((row: EnforceableArrangement) => {
    setData(prev => produce(prev, draft => {
      const i = draft.findIndex(d => d._id === row._id);
      if (i === -1) return;
      if (draft[i].id) {
        draft[i].recordAction = recordActionAsEnum('Delete');
      } else {
        draft.splice(i, 1);
      }
    }));
  }, []);

 const dataView = useMemo(() => {
  console.log('dataView recomputing, data:', data);
  return data
    .filter(d => recordActionFromEnum(d.recordAction) !== 'Delete')
    .map(row => {
      const validationResults = validateStorage(row); // ← no productStatus
 console.log('row:', row, 'validationResults:', validationResults);
     
      const validations = validationResults.reduce((map, validationResult) => ({
        ...map,
        [validationResult.key]: {
          inline: getStorageInlineMessage(validationResult),
          notification: {
            parts: getStorageNotificationMessage(
              validationResult.key,
              () => focusFieldCtrl.setFocusedField({
                _id: row._id,
                field: validationResult.key,
                kind: 'data',
              }),
              validationResult  // ← no productName
            ),
          },
          validationResult,
        },
      }), {} as Record<StorageValidationField, FieldValidation<StorageValidationResult>>);
console.log('validations built:', validations);
      return { data: row, validations };
    });
}, [data, focusFieldCtrl]); 

  const rows = useMemo(
    () => dataView,
    [dataView]
  );

  const requestEntities = useMemo(() =>
    cleanSubmissionEntityList(data).map(item => {
      const { _key, _id, ...rest } = item as EnforceableArrangement;
      return rest;
    }),
    [data]
  );

  const anyInvalid = useMemo(
    () => dataView.some(v => !isEmpty(v.validations)),
    [dataView]
  );

  return useMemo(() => ({
    add,
    anyInvalid,
    remove,
    requestEntities,
    rows,
    update,
  }), [add, anyInvalid, remove, requestEntities, rows, update]);
}

export default useEnforceableArrangements;
export type UseEnforceableArrangements = ReturnType<typeof useEnforceableArrangements>;